/**
 * Country utilities for onboarding and settings
 * Uses i18n-iso-countries for localized country names
 * Fetches supported countries from backend API
 */

import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import svLocale from 'i18n-iso-countries/langs/sv.json';

// Register locale data
countries.registerLocale(enLocale);
countries.registerLocale(svLocale);

/**
 * Get all ISO countries with localized names
 * @param {string} language - 'en' or 'sv'
 * @returns {Array} Array of { code, name } objects
 */
export function getAllCountries(language = 'en') {
  const codes = countries.getAlpha2Codes();
  return Object.keys(codes)
    .map(code => ({
      code,
      name: countries.getName(code, language) || code
    }))
    .sort((a, b) => a.name.localeCompare(b.name, language));
}

/**
 * Get country name by code
 * @param {string} code - ISO 3166-1 alpha-2 code
 * @param {string} language - 'en' or 'sv'
 * @returns {string} Localized country name or the code itself
 */
export function getCountryName(code, language = 'en') {
  if (!code) return '';
  return countries.getName(code, language) || code;
}

/**
 * Check if a country code is valid ISO 3166-1 alpha-2
 * @param {string} code - Country code to validate
 * @returns {boolean}
 */
export function isValidCountryCode(code) {
  if (!code || typeof code !== 'string') return false;
  return countries.isValid(code);
}

/**
 * Enrich country codes with localized names
 * Used to convert API response (just codes) to display-ready objects
 * @param {Array<string>} codes - Array of country codes
 * @param {string} language - 'en' or 'sv'
 * @returns {Array} Array of { code, name } objects
 */
export function enrichCountryCodes(codes, language = 'en') {
  return codes
    .map(code => ({
      code,
      name: countries.getName(code, language) || code
    }))
    .sort((a, b) => a.name.localeCompare(b.name, language));
}

// Cache for supported countries
let supportedCountriesCache = null;
let cacheTimestamp = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch supported countries from API and enrich with localized names
 * Results are cached for 5 minutes
 * @param {string} language - 'en' or 'sv'
 * @param {Object} api - API client instance (passed to avoid circular dependency)
 * @returns {Promise<Array>} Array of { code, name } objects
 */
export async function getSupportedCountries(language = 'en', api) {
  const now = Date.now();

  // Check cache
  if (supportedCountriesCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_TTL) {
    return enrichCountryCodes(supportedCountriesCache, language);
  }

  try {
    const response = await api.get('/config/supported-countries');
    supportedCountriesCache = response.data;
    cacheTimestamp = now;
    return enrichCountryCodes(response.data, language);
  } catch (error) {
    console.error('Failed to fetch supported countries:', error);
    // Return cached data if available, even if stale
    if (supportedCountriesCache) {
      return enrichCountryCodes(supportedCountriesCache, language);
    }
    // Fallback to empty array
    return [];
  }
}

/**
 * Clear the supported countries cache
 * Call this when admin updates supported countries
 */
export function clearSupportedCountriesCache() {
  supportedCountriesCache = null;
  cacheTimestamp = null;
}
